import { useState } from "react";

import { Note } from "../components/Note";
import { parseAuthorId, useAuthorBlock } from "../hooks/useAuthorBlock";
import { parseChatId, useChatBlock } from "../hooks/useChatBlock";
import { useScraperSearch } from "../hooks/useScraperSearch";
import { t } from "../i18n/strings";

export function ScrapersScreen() {
  const search = useScraperSearch();
  const block = useChatBlock();
  const author = useAuthorBlock();
  const [row, setRow] = useState("");
  const [blockRow, setBlockRow] = useState("");
  const [reason, setReason] = useState("");
  const [unblockInput, setUnblockInput] = useState("");
  const [authorInput, setAuthorInput] = useState("");

  const authorId = parseAuthorId(authorInput);
  const canActOnAuthor = authorId !== null && !author.working;

  const unblockChatId = parseChatId(unblockInput);
  const canUnblock = unblockChatId !== null && !block.unblocking;

  const canSubmit = row.trim().length > 0 && !search.working;

  // Blocking acts on a chat_id, never on what was typed, so it needs a resolve of its own - and
  // its own, not the one above: adding a scraper somewhere must not arm the button that takes a
  // chat off monitoring. Until this section's resolve has answered there is nothing to block,
  // which is what disables the button rather than hiding the section.
  const blockChatId = block.resolved?.chat_id ?? null;
  const canLook = blockChatId !== null && !block.working;
  const blockTitle = block.resolved?.chat_title ?? block.resolved?.title ?? blockRow;

  return (
    <section className="screen">
      <h2 className="screen__title">{t("scrapers.title")}</h2>
      <p className="screen__hint">{t("scrapers.hint")}</p>

      <div className="lookup">
        <input
          className="lookup__input"
          value={row}
          placeholder={t("scrapers.placeholder")}
          onChange={(event) => setRow(event.target.value)}
        />
        <button type="button" className="button" disabled={!canSubmit} onClick={() => search.resolve(row.trim())}>
          {t("scrapers.resolve")}
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={!canSubmit}
          onClick={() => search.add(row.trim())}
        >
          {t("scrapers.add")}
        </button>
      </div>

      {/* the search walks the dialogs of every scraper account, so it really does take minutes */}
      {search.working ? <Note>{t("scrapers.searching")}</Note> : null}
      {search.error ? <Note kind="error">{search.error}</Note> : null}

      {search.resolved ? (
        <Note kind="info">
          {t("scrapers.resolved", { title: search.resolved.chat_title ?? search.resolved.title ?? row })}
        </Note>
      ) : null}

      {search.added ? (
        <>
          <Note kind="success">
            {t("scrapers.added", {
              number: search.added.number ?? "?",
              title: search.added.chat_title ?? search.added.title ?? row,
              chatId: search.added.chat_id ?? "?",
            })}
          </Note>
          <Note>{t("scrapers.addedShort")}</Note>
        </>
      ) : null}

      <div className="block-chat">
        <h3 className="screen__title">{t("block.title")}</h3>
        <p className="screen__hint">{t("block.hint")}</p>

        {/* its own link field: this section is not a continuation of the one above */}
        <div className="lookup">
          <input
            className="lookup__input"
            value={blockRow}
            placeholder={t("block.placeholder")}
            onChange={(event) => setBlockRow(event.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={blockRow.trim().length === 0 || block.working}
            onClick={() => block.resolve(blockRow.trim())}
          >
            {t("block.resolve")}
          </button>
        </div>

        {block.resolved ? <Note kind="info">{t("block.resolved", { title: blockTitle })}</Note> : null}

        {block.preview === null && block.blocked === null ? (
          <div className="lookup">
            <input
              className="lookup__input"
              value={reason}
              placeholder={t("block.reasonPlaceholder")}
              onChange={(event) => setReason(event.target.value)}
            />
            <button type="button" className="button" disabled={!canLook} onClick={() => block.look(blockChatId!)}>
              {t("block.look")}
            </button>
          </div>
        ) : null}

        {block.error ? <Note kind="error">{block.error}</Note> : null}

        {block.preview ? (
          block.preview.is_blocked ? (
            <Note kind="info">{t("block.alreadyBlocked")}</Note>
          ) : (
            <>
              {/* the three counts are the entire point of confirming rather than acting */}
              <Note kind="warning">
                {t("block.trackings", { count: block.preview.trackings_count })}
                <br />
                {t("block.clients", { count: block.preview.clients_count })}
                <br />
                {t("block.notifications", { count: block.preview.notifications_count })}
              </Note>
              <div className="lookup">
                <button type="button" className="button" disabled={block.working} onClick={() => block.reset()}>
                  {t("block.cancel")}
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={block.working}
                  onClick={() => block.block(block.preview!.chat_id, reason.trim())}
                >
                  {block.working ? t("block.working") : t("block.confirm")}
                </button>
              </div>
            </>
          )
        ) : null}

        {block.blocked ? (
          <Note kind="success">{t("block.done", { title: block.blocked.chat_title ?? blockTitle })}</Note>
        ) : null}
      </div>

      <div className="unblock-chat">
        <h3 className="screen__title">{t("unblock.title")}</h3>
        <p className="screen__hint">{t("unblock.hint")}</p>

        <div className="lookup">
          <input
            className="lookup__input"
            inputMode="numeric"
            value={unblockInput}
            placeholder={t("unblock.placeholder")}
            onChange={(event) => setUnblockInput(event.target.value)}
          />
          <button
            type="button"
            className="button button--primary"
            disabled={!canUnblock}
            onClick={() => block.unblock(unblockChatId!)}
          >
            {block.unblocking ? t("unblock.working") : t("unblock.button")}
          </button>
        </div>

        {block.unblockError ? <Note kind="error">{block.unblockError}</Note> : null}
        {block.unblocked === "done" ? (
          <>
            <Note kind="success">{t("unblock.done")}</Note>
            {/* it undoes the blocking and nothing else blocking did */}
            <Note kind="warning">{t("unblock.warning")}</Note>
          </>
        ) : null}
        {block.unblocked === "not_blocked" ? <Note kind="info">{t("unblock.notBlocked")}</Note> : null}
      </div>

      <div className="block-author">
        <h3 className="screen__title">{t("authors.title")}</h3>
        <p className="screen__hint">{t("authors.hint")}</p>

        <div className="lookup">
          <input
            className="lookup__input"
            inputMode="numeric"
            value={authorInput}
            placeholder={t("authors.placeholder")}
            onChange={(event) => setAuthorInput(event.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={!canActOnAuthor}
            onClick={() => author.unblock(authorId!)}
          >
            {t("authors.unblock")}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!canActOnAuthor}
            onClick={() => author.block(authorId!)}
          >
            {author.working ? t("authors.working") : t("authors.block")}
          </button>
        </div>

        {author.error ? <Note kind="error">{author.error}</Note> : null}
        {author.result === "blocked" ? <Note kind="success">{t("authors.blocked")}</Note> : null}
        {author.result === "already" ? <Note kind="info">{t("authors.already")}</Note> : null}
        {/* shown after every addition, "already" included: the list exists, the filter does not */}
        {author.result === "blocked" || author.result === "already" ? (
          <Note kind="warning">{t("authors.notEnforced")}</Note>
        ) : null}
        {author.result === "unblocked" ? <Note kind="success">{t("authors.unblocked")}</Note> : null}
        {author.result === "not_found" ? <Note kind="info">{t("authors.notFound")}</Note> : null}
      </div>
    </section>
  );
}
